import { movies, type Movie } from '@/mocks/movies';
import { actors, type Actor } from '@/mocks/actors';

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((movie) => movie.id === id);
}

export function getActorById(id: string | undefined): Actor | undefined {
  if (!id) return undefined;
  return actors.find((actor) => actor.id === id);
}

export function getActorsByIds(ids: string[]): Actor[] {
  return ids
    .map((id) => actors.find((actor) => actor.id === id))
    .filter((actor): actor is Actor => Boolean(actor));
}

export function getMoviesByActor(actorId: string): Movie[] {
  return movies.filter((movie) => movie.cast.includes(actorId));
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  return movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((g) => movie.genres.includes(g)).length;
      const sharedCast = item.cast.filter((c) => movie.cast.includes(c)).length;
      return { item, score: sharedGenres * 2 + sharedCast * 3 };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.hot - a.item.hot)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function searchMovies(keyword: string): Movie[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return movies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.originalTitle,
      movie.director,
      movie.region,
      movie.genres.join(' '),
      movie.summary,
      getActorsByIds(movie.cast).map((a) => a.name).join(' '),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(key);
  });
}

export interface FilterOptions {
  type: 'movie' | 'tv';
  genre: string;
  year: string;
  region: string;
  episodes: string;
  sort: string;
}

export function getYears(type: 'movie' | 'tv'): number[] {
  const years = movies.filter((m) => m.type === type).map((m) => m.year);
  return Array.from(new Set(years)).sort((a, b) => b - a);
}

export function filterMovies(options: FilterOptions): Movie[] {
  const { type, genre, year, region, episodes, sort } = options;
  let list = movies.filter((movie) => movie.type === type);
  if (genre && genre !== '全部') list = list.filter((movie) => movie.genres.includes(genre));
  if (year && year !== '全部') list = list.filter((movie) => String(movie.year) === year);
  if (region && region !== '全部') list = list.filter((movie) => movie.region === region);
  if (episodes && episodes !== '全部') {
    if (episodes === '30以上') list = list.filter((movie) => movie.episodes > 30);
    else if (episodes === '21-30') list = list.filter((movie) => movie.episodes >= 21 && movie.episodes <= 30);
    else if (episodes === '12以下') list = list.filter((movie) => movie.episodes <= 12);
  }

  if (sort === '最新') {
    list = [...list].sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
  } else if (sort === '评分') {
    list = [...list].sort((a, b) => b.rating - a.rating);
  } else {
    list = [...list].sort((a, b) => b.hot - a.hot);
  }
  return list;
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.releaseDate.localeCompare(a.releaseDate)).slice(0, limit);
}

export function getHotMovies(limit = 6): Movie[] {
  return [...movies].sort((a, b) => b.hot - a.hot).slice(0, limit);
}

export function getFeaturedMovies(): Movie[] {
  return ['star-ocean', 'beauty-and-the-beast', 'city-inside', 'changan-hours']
    .map((id) => movies.find((movie) => movie.id === id))
    .filter((movie): movie is Movie => Boolean(movie));
}

export function formatMeta(movie: Movie): string {
  if (movie.type === 'movie') return `片长 ${movie.duration} 分钟`;
  return `全 ${movie.episodes} 集 · 每集 ${movie.duration} 分钟`;
}